import React, { useState, useEffect } from 'react';
import './UserJourneyTimeline.css';
import { adminSessionHeaders } from '../lib/api';

// Stages whose story is "what did we email them, and what goes out next".
const EMAIL_STAGE_KEYS = new Set(['welcome_sent', 'delivered', 'opened', 'clicked']);

const NUDGE_STYLE = {
  sent: { tag: 'Sent', color: '#6ee7b7', bg: 'rgba(6, 78, 59, 0.6)', border: '#059669' },
  covered: { tag: 'Covered', color: '#7dd3fc', bg: 'rgba(12, 74, 110, 0.5)', border: '#0284c7' },
  due: { tag: 'Due now', color: '#fbbf24', bg: 'rgba(120, 53, 15, 0.5)', border: '#d97706' },
  waiting: { tag: 'Scheduled', color: '#94a3b8', bg: 'rgba(30, 41, 59, 0.8)', border: '#334155' },
  blocked: { tag: 'Queued', color: '#64748b', bg: 'rgba(30, 41, 59, 0.8)', border: '#334155' },
  skipped: { tag: 'Not needed', color: '#64748b', bg: 'rgba(30, 41, 59, 0.8)', border: '#334155' },
  pending: { tag: 'Not yet', color: '#94a3b8', bg: 'rgba(30, 41, 59, 0.8)', border: '#334155' },
};

const NUDGE_ICON = {
  sent: 'fas fa-check',
  covered: 'fas fa-link',
  due: 'fas fa-paper-plane',
  waiting: 'fas fa-clock',
  blocked: 'fas fa-lock',
  skipped: 'fas fa-minus',
  pending: 'fas fa-envelope',
};

const STATE_STYLE = {
  complete: { tag: 'Done', color: '#6ee7b7', bg: 'rgba(6, 78, 59, 0.6)', border: '#059669' },
  partial: { tag: 'Inferred', color: '#fcd34d', bg: 'rgba(69, 26, 3, 0.7)', border: '#d97706' },
  blocked: { tag: 'Stuck here', color: '#fbbf24', bg: 'rgba(120, 53, 15, 0.5)', border: '#d97706' },
  pending: { tag: 'Not yet', color: '#94a3b8', bg: 'rgba(30, 41, 59, 0.7)', border: '#334155' },
  locked: { tag: 'Locked', color: '#64748b', bg: 'rgba(30, 41, 59, 0.7)', border: '#334155' },
};

function formatWhen(at) {
  if (!at) return '';
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
}

function formatFull(at) {
  if (!at) return null;
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
}

function relativeDays(iso) {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days > 1) return `${days} days ago`;
  if (days === 1) return 'yesterday';
  if (days === 0) return 'today';
  if (days === -1) return 'tomorrow';
  return `in ${Math.abs(days)} days`;
}

function nudgeWhen(step) {
  if (step.status === 'sent') return formatWhen(step.sentAt);
  if (step.status === 'due' && step.dueAt) return `due ${relativeDays(step.dueAt)}`;
  if (step.status === 'waiting' && step.wait) {
    return `in ${step.wait.daysRemaining}d`;
  }
  if (step.status === 'blocked' && step.blockedBy) return `after ${step.blockedBy}`;
  return '';
}

/**
 * The follow-up chain hanging off the email stages: which nudges went out, which is
 * next, and how many days until the drip sends it by itself. Hovering a bubble shows
 * the actual email, merged for this recipient, so nobody has to guess what the
 * template renders as.
 */
function NudgeRail({ userId, onSent }) {
  const [sequence, setSequence] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    return fetch(`/api/communications/users/${userId}/sequence`, { headers: { ...adminSessionHeaders() } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((body) => setSequence(body.data || null))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  async function sendStep(step) {
    const label = step.code;
    if (!window.confirm(
      `Send ${label} to ${sequence.recipient.email} now?\n\n`
      + `Subject: ${(step.preview && step.preview.subject) || step.label}`
    )) return;

    setSending(true);
    setNotice(null);
    try {
      const r = await fetch(`/api/communications/users/${userId}/sequence/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...adminSessionHeaders() },
        body: JSON.stringify({ stepId: step.stepId }),
      });
      const body = await r.json();
      if (!r.ok || !body.success) {
        setNotice({ kind: 'error', text: body.error || `Could not send ${label} (${body.skipped || r.status}).` });
      } else {
        setNotice({ kind: 'ok', text: `${label} sent to ${body.sent.email}.` });
        setSequence(body.data || sequence);
        if (onSent) onSent();
      }
    } catch (err) {
      setNotice({ kind: 'error', text: err.message });
    } finally {
      setSending(false);
    }
  }

  if (loading && !sequence) {
    return (
      <div className="ujt-nudges">
        <div className="ujt-nudge-head"><i className="fas fa-spinner fa-spin" /> Loading follow-up emails…</div>
      </div>
    );
  }
  if (error || !sequence) {
    return (
      <div className="ujt-nudges">
        <div className="ujt-nudge-head">
          <i className="fas fa-triangle-exclamation" style={{ color: '#f59e0b' }} />
          Could not load the follow-up sequence{error ? ` (${error})` : ''}.
        </div>
      </div>
    );
  }

  const steps = sequence.steps || [];
  const nextDue = steps.find((s) => s.status === 'due');
  const hoveredStep = hovered ? steps.find((s) => s.stepId === hovered) : null;

  return (
    <div className="ujt-nudges">
      <div className="ujt-nudge-head">
        <i className="fas fa-envelope-open-text" />
        Follow-up emails
        <span className="ujt-cadence">{sequence.cadenceDays}-DAY CADENCE · AUTOMATIC</span>
        <span className="ujt-nudge-position">{sequence.positionLabel}</span>
      </div>
      <div className="ujt-nudge-sub">
        Hover a bubble to read the email as this recipient will receive it. The drip sends the next
        one on its own each morning; the button sends it now.
      </div>

      <div className="ujt-nudge-rail">
        {steps.map((s, i) => {
          const style = NUDGE_STYLE[s.status] || NUDGE_STYLE.pending;
          const isSent = s.status === 'sent' || s.status === 'covered';
          return (
            <div
              key={s.stepId}
              className={`ujt-nudge is-${s.status}`}
              onMouseEnter={() => setHovered(s.stepId)}
              onMouseLeave={() => setHovered((h) => (h === s.stepId ? null : h))}
            >
              <div className="ujt-nudge-bubble-row">
                <span className={`ujt-connector ${i === 0 ? 'is-hidden' : isSent || steps[i - 1].status === 'sent' || steps[i - 1].status === 'covered' ? 'is-filled' : 'is-dashed'}`} />
                <span
                  className="ujt-nudge-bubble"
                  style={{ background: style.bg, color: style.color, border: `2px solid ${style.border}` }}
                >
                  <i className={NUDGE_ICON[s.status] || NUDGE_ICON.pending} />
                </span>
                <span className={`ujt-connector ${i === steps.length - 1 ? 'is-hidden' : isSent ? 'is-filled' : 'is-dashed'}`} />
              </div>
              <div className="ujt-nudge-code">{s.code}</div>
              <div className="ujt-nudge-label">{s.label}</div>
              <div className="ujt-nudge-when" style={{ color: style.color }}>{nudgeWhen(s)}</div>
              {s.status === 'due' && (
                <button
                  type="button"
                  className="ujt-send-btn"
                  disabled={sending}
                  onClick={() => sendStep(s)}
                >
                  {sending ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-paper-plane" />}
                  {' '}Send now
                </button>
              )}

              {hovered === s.stepId && s.preview && s.preview.html && (
                <div className={`ujt-mini${i === 0 ? ' at-start' : i === steps.length - 1 ? ' at-end' : ''}`}>
                  <div className="ujt-mini-subject">{s.preview.subject}</div>
                  <div className="ujt-mini-frame">
                    <iframe
                      title={`${s.code} preview`}
                      className="ujt-mini-iframe"
                      sandbox=""
                      srcDoc={s.preview.html}
                    />
                  </div>
                  <div className="ujt-mini-foot">
                    {s.preview.templateName || s.preview.templateId}
                    {s.trigger ? ` · sent ${s.trigger}ly` : ''}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {notice && (
        <div className={`ujt-notice is-${notice.kind}`}>
          <i className={notice.kind === 'ok' ? 'fas fa-circle-check' : 'fas fa-circle-exclamation'} />
          {' '}{notice.text}
        </div>
      )}

      <div className="ujt-nudge-foot">
        {hoveredStep && hoveredStep.evidence
          ? <span className="ujt-detail-meta">{hoveredStep.code} — {hoveredStep.evidence}</span>
          : nextDue
            ? <span>
                <strong style={{ color: '#fbbf24' }}>{nextDue.code}</strong> is overdue
                {nextDue.dueAt ? ` (was due ${relativeDays(nextDue.dueAt)})` : ''} — the drip will send it
                at the next daily run, or send it now.
              </span>
            : <span>Nothing due. The next nudge unlocks {sequence.cadenceDays} days after the last one was sent.</span>}
      </div>
    </div>
  );
}

/**
 * The user's lifecycle on OptiPrime as a horizontal rail of bubbles that unlock
 * left to right. Green means the stage is evidenced, amber means it happened but
 * the evidence has a caveat (e.g. a mail-provider proxy fetched the tracking
 * pixel rather than a person opening the mail), dashed amber marks the stage the
 * user is currently stuck behind, and grey is not reached yet.
 */
export default function UserJourneyTimeline({ userId }) {
  const [journey, setJourney] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);

  const load = React.useCallback((keepSelection = false) => {
    if (!userId) return Promise.resolve();
    setError(null);
    return fetch(`/api/community/users/${userId}/journey`, { headers: { ...adminSessionHeaders() } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((body) => {
        const data = body && body.data;
        setJourney(data || null);
        if (keepSelection) return;
        // Open on the blocker if there is one, else on the furthest stage reached.
        const stages = (data && data.stages) || [];
        const blocked = stages.find((s) => s.state === 'blocked');
        const reached = [...stages].reverse().find((s) => s.state === 'complete' || s.state === 'partial');
        setSelectedKey((blocked || reached || stages[0] || {}).key || null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  // A nudge that just went out changes the journey too (Welcome Email Sent gains a send).
  const reload = React.useCallback(() => load(true), [load]);

  if (loading) {
    return (
      <div className="ujt">
        <div className="ujt-state-msg">
          <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }} />
          Building journey…
        </div>
      </div>
    );
  }

  if (error || !journey) {
    return (
      <div className="ujt">
        <div className="ujt-state-msg">
          <i className="fas fa-triangle-exclamation" style={{ marginRight: 8, color: '#f59e0b' }} />
          Could not load this user's journey{error ? ` (${error})` : ''}.
        </div>
      </div>
    );
  }

  const { stages = [], progress = {}, counts = {}, alerts = [] } = journey;
  const lastReachedIndex = stages.reduce(
    (acc, s, i) => (s.state === 'complete' || s.state === 'partial' ? i : acc),
    -1
  );
  const selected = stages.find((s) => s.key === selectedKey) || stages[0];
  const selectedStyle = selected ? (STATE_STYLE[selected.state] || STATE_STYLE.pending) : STATE_STYLE.pending;

  return (
    <div className="ujt">
      <div className="ujt-head">
        <i className="fas fa-route ujt-head-icon" />
        Journey on OptiPrime
        <span className="ujt-progress-pill">
          {progress.completed}/{progress.total} UNLOCKED
        </span>
        {progress.stuckAt && (
          <span className="ujt-stuck-pill">
            <i className="fas fa-hourglass-half" style={{ marginRight: 5 }} />
            WAITING ON: {(stages.find((s) => s.key === progress.stuckAt) || {}).label}
          </span>
        )}
      </div>
      <div className="ujt-subhead">
        Every stage this person has reached, newest evidence on the right. Click a bubble for what proves it.
        {counts.logins === 0 && counts.welcomeSends > 0 && ' Credentials are out; nothing has come back yet.'}
      </div>

      <div className="ujt-rail">
        {stages.map((s, i) => {
          const style = STATE_STYLE[s.state] || STATE_STYLE.pending;
          // A connector is filled only while it sits behind the furthest stage reached.
          const leftFilled = i > 0 && i <= lastReachedIndex;
          const rightFilled = i < lastReachedIndex;
          return (
            <button
              key={s.key}
              type="button"
              className={`ujt-stage is-${s.state}${selectedKey === s.key ? ' is-selected' : ''}`}
              onClick={() => setSelectedKey(s.key)}
              title={`${s.label} — ${style.tag}`}
            >
              <div className="ujt-bubble-row">
                <span className={`ujt-connector ${i === 0 ? 'is-hidden' : leftFilled ? 'is-filled' : 'is-dashed'}`} />
                <span className={`ujt-bubble is-${s.state}`}>
                  <i className={s.state === 'locked' ? 'fas fa-lock' : s.icon} />
                </span>
                <span className={`ujt-connector ${i === stages.length - 1 ? 'is-hidden' : rightFilled ? 'is-filled' : 'is-dashed'}`} />
              </div>
              <div className="ujt-label">{s.label}</div>
              <div className="ujt-when">{formatWhen(s.at)}</div>
              {EMAIL_STAGE_KEYS.has(s.key) && (
                <i className="fas fa-angle-down ujt-has-children" title="Opens the follow-up email chain" />
              )}
            </button>
          );
        })}
      </div>

      {selected && (
        <div className="ujt-detail">
          <span
            className="ujt-detail-icon"
            style={{
              background: selectedStyle.bg,
              color: selectedStyle.color,
              border: `1px solid ${selectedStyle.border}`,
            }}
          >
            <i className={selected.icon} />
          </span>
          <div style={{ flex: 1 }}>
            <div className="ujt-detail-title">
              {selected.label}
              <span
                className="ujt-state-tag"
                style={{
                  background: selectedStyle.bg,
                  color: selectedStyle.color,
                  border: `1px solid ${selectedStyle.border}`,
                }}
              >
                {selectedStyle.tag}
              </span>
              {formatFull(selected.at) && (
                <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)' }}>
                  {formatFull(selected.at)}
                </span>
              )}
            </div>
            <div className="ujt-detail-body">{selected.detail}</div>
            {selected.evidence && <div className="ujt-detail-meta">source: {selected.evidence}</div>}
          </div>
        </div>
      )}

      {selected && EMAIL_STAGE_KEYS.has(selected.key) && (
        <NudgeRail userId={userId} onSent={reload} />
      )}

      {alerts.length > 0 && (
        <div className="ujt-alerts">
          {alerts.map((a, i) => (
            <div className="ujt-alert-row" key={i}>
              <i className="fas fa-bell" />
              <span>
                {formatFull(a.occurred_at)} — {a.summary}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
